import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { throwIfError } from "@/lib/api/_supabaseErrors";
import { chunkText } from "@/lib/knowledge/chunkText";
import {
  contactSourceText,
  dailyLogSourceText,
  expenseSummarySourceText,
  factSourceText,
  phaseMaterialsSourceText,
  phaseSourceText,
  quoteSourceText,
  taskSourceText,
  type ExpenseSummaryRow
} from "@/lib/knowledge/sourceText";
import { stableUuidFromKey } from "@/lib/knowledge/stableId";
import {
  CONTEXT_CHAR_BUDGET,
  filterChunksByPin,
  isPinActive,
  rankChunks,
  sanitizeSearchQuery,
  trimToBudget,
  type RankedChunk,
  type SourcePin
} from "@/lib/knowledge/rank";
import {
  chunkSpreadsheetPlainText,
  chunkSpreadsheetText,
  documentKindFromUrl,
  fetchWorkbookFromUrl,
  sheetsToPlainText
} from "@/lib/excelText";
import {
  enrichBoqSearchWithSheetMeta,
  extractBoqFromWorkbook,
  type BoqExtractedItem
} from "@/lib/knowledge/boqExtract";
import { expandSearchQuery, isQuantityOrCostQuestion, buildRetrievalQuery } from "@/lib/knowledge/expandQuery";
import { extractPdfTextFromUrl, isExtractedTextEmpty } from "@/lib/pdfText";
import type { Tables } from "@/types/database";

export type KnowledgeSourceType = RankedChunk["source_type"];
export type { RankedChunk, SourcePin };

export type KnowledgeCitation = {
  source_type: KnowledgeSourceType | "web";
  source_id: string;
  title: string;
  url?: string;
};

export type KnowledgeSource = {
  source_type: KnowledgeSourceType;
  source_id: string;
  title: string;
  category: string | null;
  extraction_status: Tables<"documents">["extraction_status"];
  phase_id: string | null;
};

export type BoqHit = {
  id: string;
  document_id: string;
  document_title: string;
  sheet_name: string;
  section: string;
  code: string;
  name: string;
  unit: string;
  quantity: number | null;
  unit_price: number | null;
  total: number | null;
  row_index: number;
  item_type: string;
  rank: number;
};

export type NotebookRetrieval = {
  chunks: RankedChunk[];
  boqItems: BoqHit[];
};

const BOQ_CHAR_BUDGET = 6_000;
const RAG_BUDGET_WITH_BOQ = 10_000;

async function replaceSourceChunks(args: {
  source_type: KnowledgeSourceType;
  source_id: string;
  title: string;
  category?: string | null;
  content?: string;
  parts?: string[];
}): Promise<void> {
  const { error: delErr } = await supabase
    .from("knowledge_chunks")
    .delete()
    .eq("source_type", args.source_type)
    .eq("source_id", args.source_id);
  throwIfError(delErr);

  const parts = args.parts ?? chunkText(args.content ?? "");
  if (parts.length === 0) return;

  const { error: insErr } = await supabase.from("knowledge_chunks").insert(
    parts.map((content, chunk_index) => ({
      source_type: args.source_type,
      source_id: args.source_id,
      title: args.title,
      chunk_index,
      content,
      category: args.category ?? null
    }))
  );
  throwIfError(insErr);
}

async function replaceBoqItems(documentId: string, items: BoqExtractedItem[]): Promise<void> {
  const { error: delErr } = await supabase
    .from("project_boq_items")
    .delete()
    .eq("document_id", documentId);
  throwIfError(delErr);
  if (items.length === 0) return;

  // Batch insert — PostgREST limit körül; 500-as csomagok.
  const batchSize = 400;
  for (let i = 0; i < items.length; i += batchSize) {
    const slice = items.slice(i, i + batchSize);
    const { error: insErr } = await supabase.from("project_boq_items").insert(
      slice.map((item) => ({
        document_id: documentId,
        sheet_name: item.sheet_name,
        section: item.section,
        code: item.code,
        name: item.name,
        unit: item.unit,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total: item.total,
        row_index: item.row_index,
        item_type: item.item_type,
        search_text: item.search_text
      }))
    );
    throwIfError(insErr);
  }
}

async function deleteDocumentBoq(documentId: string): Promise<void> {
  const { error } = await supabase.from("project_boq_items").delete().eq("document_id", documentId);
  throwIfError(error);
}

export async function indexTaskRow(
  task: Pick<Tables<"tasks">, "id" | "title" | "description" | "description_summary" | "notes">
): Promise<void> {
  await replaceSourceChunks({
    source_type: "task",
    source_id: task.id,
    title: task.title,
    content: taskSourceText(task)
  });
}

export async function indexPhaseRow(
  phase: Pick<Tables<"phases">, "id" | "title" | "description" | "guide" | "status">
): Promise<void> {
  await replaceSourceChunks({
    source_type: "phase",
    source_id: phase.id,
    title: phase.title,
    content: phaseSourceText(phase)
  });
  await replaceSourceChunks({
    source_type: "material",
    source_id: phase.id,
    title: `Anyagok és eszközök — ${phase.title}`,
    content: phaseMaterialsSourceText(phase)
  });
}

export async function indexDailyLogRow(
  log: Pick<
    Tables<"site_daily_logs">,
    "id" | "log_date" | "weather_summary" | "work_summary" | "issues" | "task_id"
  >
): Promise<void> {
  let taskTitle: string | null = null;
  if (log.task_id) {
    const { data } = await supabase.from("tasks").select("title").eq("id", log.task_id).maybeSingle();
    taskTitle = data?.title ?? null;
  }
  await replaceSourceChunks({
    source_type: "daily_log",
    source_id: log.id,
    title: `Napló — ${log.log_date}`,
    content: dailyLogSourceText(log, taskTitle)
  });
}

export async function indexQuoteRow(
  quote: Pick<
    Tables<"quotes">,
    | "id"
    | "title"
    | "amount"
    | "currency"
    | "status"
    | "valid_until"
    | "notes"
    | "contact_id"
    | "task_id"
  >
): Promise<void> {
  const [contactRes, taskRes] = await Promise.all([
    quote.contact_id
      ? supabase.from("contacts").select("name,company").eq("id", quote.contact_id).maybeSingle()
      : Promise.resolve({ data: null }),
    quote.task_id
      ? supabase.from("tasks").select("title").eq("id", quote.task_id).maybeSingle()
      : Promise.resolve({ data: null })
  ]);
  const contact = contactRes.data;
  const contactName = contact
    ? [contact.name, contact.company].filter(Boolean).join(" · ")
    : null;

  await replaceSourceChunks({
    source_type: "quote",
    source_id: quote.id,
    title: `Árajánlat — ${quote.title}`,
    content: quoteSourceText(quote, { contactName, taskTitle: taskRes.data?.title ?? null })
  });
}

export async function indexContactRow(
  contact: Pick<
    Tables<"contacts">,
    "id" | "name" | "company" | "type" | "trade" | "phone" | "email" | "notes"
  >
): Promise<void> {
  await replaceSourceChunks({
    source_type: "contact",
    source_id: contact.id,
    title: `Kontakt — ${contact.name}`,
    content: contactSourceText(contact)
  });
}

export async function indexFactRow(
  fact: {
    id: string;
    kind: string | null;
    statement: string;
    details?: string | null;
    valid_from?: string | null;
    phase_id?: string | null;
    task_id?: string | null;
  }
): Promise<void> {
  const [phaseRes, taskRes] = await Promise.all([
    fact.phase_id
      ? supabase.from("phases").select("title").eq("id", fact.phase_id).maybeSingle()
      : Promise.resolve({ data: null }),
    fact.task_id
      ? supabase.from("tasks").select("title").eq("id", fact.task_id).maybeSingle()
      : Promise.resolve({ data: null })
  ]);
  await replaceSourceChunks({
    source_type: "fact",
    source_id: fact.id,
    title: `Tény — ${fact.statement.slice(0, 80)}`,
    content: factSourceText(fact, {
      phaseTitle: phaseRes.data?.title ?? null,
      taskTitle: taskRes.data?.title ?? null
    })
  });
}

type ExpenseIndexRow = ExpenseSummaryRow & { phase_id: string | null };

function expenseBuckets(
  rows: ExpenseIndexRow[],
  phaseTitles: Map<string, string>
): Array<{ key: string; label: string; rows: ExpenseIndexRow[]; itemLimit: number }> {
  const byPhase = new Map<string, ExpenseIndexRow[]>();
  const byMonth = new Map<string, ExpenseIndexRow[]>();
  for (const r of rows) {
    const phaseKey = r.phase_id ?? "none";
    const phaseList = byPhase.get(phaseKey);
    if (phaseList) phaseList.push(r);
    else byPhase.set(phaseKey, [r]);

    const monthKey = r.date.slice(0, 7);
    const monthList = byMonth.get(monthKey);
    if (monthList) monthList.push(r);
    else byMonth.set(monthKey, [r]);
  }

  const out: Array<{ key: string; label: string; rows: ExpenseIndexRow[]; itemLimit: number }> = [];
  for (const [phaseKey, list] of byPhase) {
    const label =
      phaseKey === "none" ? "Fázishoz nem kötött kiadások" : phaseTitles.get(phaseKey) ?? "Ismeretlen fázis";
    out.push({ key: `phase:${phaseKey}`, label, rows: list, itemLimit: 60 });
  }
  for (const [ym, list] of byMonth) {
    out.push({ key: `month:${ym}`, label: `${ym} hónap`, rows: list, itemLimit: 12 });
  }
  return out;
}

/** Kiadás-összesítők újraépítése. Bucketonként egy chunk, nem soronként. */
export async function reindexExpenseSummaries(): Promise<number> {
  const [{ data: expenses, error: eErr }, { data: phases, error: pErr }] = await Promise.all([
    supabase.from("expenses").select("title,amount,date,category,notes,phase_id"),
    supabase.from("phases").select("id,title")
  ]);
  throwIfError(eErr);
  throwIfError(pErr);

  const phaseTitles = new Map((phases ?? []).map((p) => [p.id, p.title]));
  const buckets = expenseBuckets(expenses ?? [], phaseTitles);

  const { error: delErr } = await supabase
    .from("knowledge_chunks")
    .delete()
    .eq("source_type", "expense");
  throwIfError(delErr);

  for (const bucket of buckets) {
    await replaceSourceChunks({
      source_type: "expense",
      source_id: stableUuidFromKey(`expense:${bucket.key}`),
      title: `Kiadások — ${bucket.label}`,
      content: expenseSummarySourceText({
        label: bucket.label,
        rows: bucket.rows,
        itemLimit: bucket.itemLimit
      })
    });
  }
  return buckets.length;
}

async function updateDocumentExtraction(
  doc: Tables<"documents">,
  values: Partial<Tables<"documents">>
): Promise<Tables<"documents">> {
  const { data, error } = await supabase
    .from("documents")
    .update(values)
    .eq("id", doc.id)
    .select("*")
    .single();
  throwIfError(error);
  return data ?? doc;
}

async function deleteDocumentChunks(documentId: string): Promise<void> {
  const { error } = await supabase
    .from("knowledge_chunks")
    .delete()
    .eq("source_type", "document")
    .eq("source_id", documentId);
  throwIfError(error);
  await deleteDocumentBoq(documentId);
}

export async function indexDocumentRow(doc: Tables<"documents">): Promise<Tables<"documents">> {
  const now = new Date().toISOString();
  const kind = documentKindFromUrl(doc.file_url);

  if (!kind) {
    await deleteDocumentChunks(doc.id);
    return updateDocumentExtraction(doc, {
      extracted_text: null,
      extraction_status: "unsupported",
      extracted_at: now,
      extraction_error: null
    });
  }

  try {
    const { createSignedDocumentUrl } = await import("./storageUrls");
    const url = (await createSignedDocumentUrl(doc.file_url)) ?? doc.file_url ?? "";

    if (kind === "xlsx") {
      const { workbook, fileName } = await fetchWorkbookFromUrl(url);
      const text = sheetsToPlainText(workbook, fileName).slice(0, 400_000);
      if (isExtractedTextEmpty(text)) {
        await deleteDocumentChunks(doc.id);
        return updateDocumentExtraction(doc, {
          extracted_text: text || null,
          extraction_status: "empty",
          extracted_at: now,
          extraction_error: null
        });
      }

      const parts = chunkSpreadsheetText(workbook, fileName);
      await replaceSourceChunks({
        source_type: "document",
        source_id: doc.id,
        title: doc.title,
        category: doc.category,
        parts
      });

      const boq = enrichBoqSearchWithSheetMeta(workbook, extractBoqFromWorkbook(workbook));
      await replaceBoqItems(doc.id, boq);

      return updateDocumentExtraction(doc, {
        extracted_text: text,
        extraction_status: "ready",
        extracted_at: now,
        extraction_error: null
      });
    }

    const text = await extractPdfTextFromUrl(url);

    if (isExtractedTextEmpty(text)) {
      await deleteDocumentChunks(doc.id);
      return updateDocumentExtraction(doc, {
        extracted_text: text || null,
        extraction_status: "empty",
        extracted_at: now,
        extraction_error: null
      });
    }

    await replaceSourceChunks({
      source_type: "document",
      source_id: doc.id,
      title: doc.title,
      category: doc.category,
      content: text
    });
    await deleteDocumentBoq(doc.id);

    return updateDocumentExtraction(doc, {
      extracted_text: text,
      extraction_status: "ready",
      extracted_at: now,
      extraction_error: null
    });
  } catch (e) {
    const message = e instanceof Error ? e.message : "Ismeretlen indexelési hiba";
    return updateDocumentExtraction(doc, {
      extraction_status: "error",
      extracted_at: now,
      extraction_error: message.slice(0, 500)
    });
  }
}

export async function syncDocumentKnowledge(
  doc: Tables<"documents">,
  mode: "extract" | "metadata"
): Promise<Tables<"documents">> {
  if (mode === "extract") return indexDocumentRow(doc);
  if (doc.extraction_status === "ready" && doc.extracted_text) {
    const kind = documentKindFromUrl(doc.file_url);
    const sheetParts =
      kind === "xlsx" ? chunkSpreadsheetPlainText(doc.extracted_text) : [];
    await replaceSourceChunks({
      source_type: "document",
      source_id: doc.id,
      title: doc.title,
      category: doc.category,
      ...(sheetParts.length > 0 ? { parts: sheetParts } : { content: doc.extracted_text })
    });
  }
  return doc;
}

export type ReindexResult = {
  documents: number;
  tasks: number;
  phases: number;
  dailyLogs: number;
  quotes: number;
  contacts: number;
  facts: number;
  expenseBuckets: number;
};

export async function reindexAllKnowledge(options?: { force?: boolean }): Promise<ReindexResult> {
  const force = Boolean(options?.force);
  const [
    { data: docs, error: dErr },
    { data: tasks, error: tErr },
    { data: phases, error: pErr },
    { data: logs, error: lErr },
    { data: quotes, error: qErr },
    { data: contacts, error: cErr },
    { data: facts, error: fErr },
    { data: existing, error: eErr }
  ] = await Promise.all([
    supabase.from("documents").select("*"),
    supabase.from("tasks").select("id,title,description,description_summary,notes"),
    supabase.from("phases").select("id,title,description,guide,status"),
    supabase
      .from("site_daily_logs")
      .select("id,log_date,weather_summary,work_summary,issues,task_id"),
    supabase
      .from("quotes")
      .select("id,title,amount,currency,status,valid_until,notes,contact_id,task_id"),
    supabase.from("contacts").select("id,name,company,type,trade,phone,email,notes"),
    supabase
      .from("project_facts")
      .select("id,kind,statement,details,valid_from,phase_id,task_id")
      .is("superseded_by", null),
    supabase.from("knowledge_chunks").select("source_type, source_id")
  ]);
  throwIfError(dErr);
  throwIfError(tErr);
  throwIfError(pErr);
  throwIfError(lErr);
  throwIfError(qErr);
  throwIfError(cErr);
  throwIfError(fErr);
  throwIfError(eErr);

  const have = new Set((existing ?? []).map((c) => `${c.source_type}:${c.source_id}`));

  let documents = 0;
  for (const doc of docs ?? []) {
    const stale =
      force ||
      doc.extraction_status === null ||
      doc.extraction_status === "pending" ||
      doc.extraction_status === "error" ||
      // Korábban nem támogatott fájltípusok (pl. Excel) újraindexelése.
      (doc.extraction_status === "unsupported" && documentKindFromUrl(doc.file_url) !== null);
    if (!stale) continue;
    await indexDocumentRow(doc);
    documents += 1;
  }

  let taskCount = 0;
  for (const task of tasks ?? []) {
    if (!force && have.has(`task:${task.id}`)) continue;
    await indexTaskRow(task);
    taskCount += 1;
  }

  let phaseCount = 0;
  for (const phase of phases ?? []) {
    if (!force && have.has(`phase:${phase.id}`) && have.has(`material:${phase.id}`)) continue;
    await indexPhaseRow(phase);
    phaseCount += 1;
  }

  let logCount = 0;
  for (const log of logs ?? []) {
    if (!force && have.has(`daily_log:${log.id}`)) continue;
    await indexDailyLogRow(log);
    logCount += 1;
  }

  let quoteCount = 0;
  for (const quote of quotes ?? []) {
    if (!force && have.has(`quote:${quote.id}`)) continue;
    await indexQuoteRow(quote);
    quoteCount += 1;
  }

  let contactCount = 0;
  for (const contact of contacts ?? []) {
    if (!force && have.has(`contact:${contact.id}`)) continue;
    await indexContactRow(contact);
    contactCount += 1;
  }

  let factCount = 0;
  for (const fact of facts ?? []) {
    if (!force && have.has(`fact:${fact.id}`)) continue;
    await indexFactRow(fact);
    factCount += 1;
  }

  // A kiadás-összesítők bucketenként épülnek, ezért csak együtt frissíthetők.
  const expenseBuckets = await reindexExpenseSummaries();

  return {
    documents,
    tasks: taskCount,
    phases: phaseCount,
    dailyLogs: logCount,
    quotes: quoteCount,
    contacts: contactCount,
    facts: factCount,
    expenseBuckets
  };
}

const KNOWN_SOURCE_TYPES: readonly string[] = [
  "document",
  "task",
  "phase",
  "daily_log",
  "expense",
  "quote",
  "contact",
  "fact",
  "material"
];

function asSourceType(v: string): KnowledgeSourceType {
  return KNOWN_SOURCE_TYPES.includes(v) ? (v as KnowledgeSourceType) : "document";
}

export async function retrieveBoqItems(query: string, limit = 15): Promise<BoqHit[]> {
  const expanded = expandSearchQuery(query);
  if (!expanded.trim()) return [];

  const { data, error } = await supabase.rpc("search_boq_items", {
    p_query: expanded,
    p_limit: limit
  });
  throwIfError(error);

  const hits: BoqHit[] = (data ?? []).map((row) => ({
    id: row.id,
    document_id: row.document_id,
    document_title: row.document_title,
    sheet_name: row.sheet_name,
    section: row.section,
    code: row.code,
    name: row.name,
    unit: row.unit,
    quantity: row.quantity === null || row.quantity === undefined ? null : Number(row.quantity),
    unit_price:
      row.unit_price === null || row.unit_price === undefined ? null : Number(row.unit_price),
    total: row.total === null || row.total === undefined ? null : Number(row.total),
    row_index: row.row_index,
    item_type: row.item_type,
    rank: row.rank
  }));

  return trimBoqToBudget(hits, BOQ_CHAR_BUDGET);
}

function formatBoqLine(item: BoqHit): string {
  const qty =
    item.quantity === null
      ? ""
      : `${item.quantity}${item.unit ? ` ${item.unit}` : ""}`;
  const total =
    item.total === null ? "" : `${item.total.toLocaleString("hu-HU")} EUR`;
  const bits = [
    item.section ? `[${item.section}]` : "",
    item.code,
    item.name,
    qty ? `— ${qty}` : "",
    total ? `(${total})` : ""
  ].filter(Boolean);
  return `- ${bits.join(" ")}`;
}

function trimBoqToBudget(items: BoqHit[], budget: number): BoqHit[] {
  const out: BoqHit[] = [];
  let used = 0;
  for (const item of items) {
    const len = formatBoqLine(item).length + (item.document_title?.length ?? 0) + 8;
    if (out.length > 0 && used + len > budget) break;
    out.push(item);
    used += len;
  }
  return out;
}

export function formatBoqBlock(items: BoqHit[]): string {
  if (items.length === 0) return "";
  const byDoc = new Map<string, BoqHit[]>();
  for (const item of items) {
    const list = byDoc.get(item.document_title) ?? [];
    list.push(item);
    byDoc.set(item.document_title, list);
  }
  const parts: string[] = [];
  for (const [title, rows] of byDoc) {
    parts.push(
      `Forrás: ${title}\n${rows.map(formatBoqLine).join("\n")}`
    );
  }
  return parts.join("\n\n");
}

export async function retrieveKnowledgeChunks(
  query: string,
  pin: SourcePin,
  options?: { charBudget?: number }
): Promise<RankedChunk[]> {
  const q = sanitizeSearchQuery(query);
  const active = isPinActive(pin);
  const budget = options?.charBudget ?? CONTEXT_CHAR_BUDGET;

  // F7.4: hibrid FTS+vektor, ha elérhető; különben klasszikus FTS RPC.
  const hybrid = await supabase.rpc("search_knowledge_hybrid", {
    p_query: q,
    p_limit: 28,
    p_embedding: null
  });

  if (!hybrid.error && hybrid.data && hybrid.data.length > 0) {
    const mapped: RankedChunk[] = hybrid.data.map((row: {
      id: string;
      source_type: string;
      source_id: string;
      title: string;
      content: string;
      score: number;
    }) => ({
      id: row.id,
      source_type: asSourceType(row.source_type),
      source_id: row.source_id,
      title: row.title,
      chunk_index: 0,
      content: row.content,
      category: null,
      rank: row.score
    }));
    const ranked = rankChunks(filterChunksByPin(mapped, pin), q);
    return trimToBudget(ranked, budget);
  }

  const { data, error } = await supabase.rpc("search_knowledge_chunks", {
    p_query: q,
    p_limit: 28,
    p_document_ids: active && pin.documentIds.length > 0 ? pin.documentIds : null,
    p_include_documents: !active || pin.documentIds.length > 0,
    p_include_tasks: !active || pin.includeTasks,
    p_include_phases: !active || pin.includePhases,
    p_include_records: !active
  });
  throwIfError(error);

  const mapped: RankedChunk[] = (data ?? []).map((row) => ({
    id: row.id,
    source_type: asSourceType(row.source_type),
    source_id: row.source_id,
    title: row.title,
    chunk_index: row.chunk_index,
    content: row.content,
    category: row.category,
    rank: row.rank
  }));

  const ranked = rankChunks(filterChunksByPin(mapped, pin), q);
  return trimToBudget(ranked, budget);
}

/**
 * BOQ először (mennyiség/ár), majd RAG.
 * A `history` kell a követő kérdésekhez („most látod?”), különben elveszik a beton/sávalap kontextus.
 */
export async function retrieveNotebookContext(
  query: string,
  pin: SourcePin,
  history: ReadonlyArray<{ role: string; content: string }> = []
): Promise<NotebookRetrieval> {
  const retrievalQuery = buildRetrievalQuery(query, history);
  const preferBoq = isQuantityOrCostQuestion(retrievalQuery);
  const expanded = expandSearchQuery(retrievalQuery);
  const boqItems = await retrieveBoqItems(retrievalQuery, preferBoq ? 20 : 12);
  const ragBudget = boqItems.length > 0 ? RAG_BUDGET_WITH_BOQ : CONTEXT_CHAR_BUDGET;
  // RAG: bővített kulcsszavak, ha a nyers követő kérdés üres találatot adna
  const ragQuery = expanded.trim() || sanitizeSearchQuery(query);
  const chunks = await retrieveKnowledgeChunks(ragQuery, pin, { charBudget: ragBudget });

  if (typeof console !== "undefined") {
    console.info("[projekt-ai:boq]", {
      question: query.slice(0, 120),
      retrievalQuery: retrievalQuery.slice(0, 240),
      expanded: expanded.slice(0, 240),
      preferBoq,
      boqCount: boqItems.length,
      topBoq: boqItems.slice(0, 5).map((b) => ({
        section: b.section,
        code: b.code,
        name: b.name.slice(0, 60),
        quantity: b.quantity,
        unit: b.unit,
        rank: b.rank
      })),
      ragCount: chunks.length
    });
  }

  return { chunks, boqItems };
}

export async function listKnowledgeSources(): Promise<KnowledgeSource[]> {
  const [{ data: docs, error: dErr }, { data: tasks, error: tErr }, { data: phases, error: pErr }] =
    await Promise.all([
      supabase.from("documents").select("id,title,category,extraction_status").order("title"),
      supabase.from("tasks").select("id,title,phase_id").order("title").limit(500),
      supabase.from("phases").select("id,title").order("order_index")
    ]);
  throwIfError(dErr);
  throwIfError(tErr);
  throwIfError(pErr);

  const out: KnowledgeSource[] = [];
  for (const d of docs ?? []) {
    out.push({
      source_type: "document",
      source_id: d.id,
      title: d.title,
      category: d.category,
      extraction_status: d.extraction_status,
      phase_id: null
    });
  }
  for (const t of tasks ?? []) {
    out.push({
      source_type: "task",
      source_id: t.id,
      title: t.title,
      category: null,
      extraction_status: "ready",
      phase_id: t.phase_id
    });
  }
  for (const p of phases ?? []) {
    out.push({
      source_type: "phase",
      source_id: p.id,
      title: p.title,
      category: null,
      extraction_status: "ready",
      phase_id: p.id
    });
  }
  return out;
}

export const knowledgeSourcesQueryOptions = () =>
  queryOptions({
    queryKey: ["knowledgeSources"] as const,
    queryFn: listKnowledgeSources
  });

export type KnowledgeCoverage = Partial<Record<KnowledgeSourceType, number>>;

/** Indexelt forrás-darabszám típusonként — a Források panel átláthatóságához. */
export async function getKnowledgeCoverage(): Promise<KnowledgeCoverage> {
  const { data, error } = await supabase.from("knowledge_chunks").select("source_type, source_id");
  throwIfError(error);
  const seen = new Map<KnowledgeSourceType, Set<string>>();
  for (const row of data ?? []) {
    const type = asSourceType(row.source_type);
    const set = seen.get(type) ?? new Set<string>();
    set.add(row.source_id);
    seen.set(type, set);
  }
  const out: KnowledgeCoverage = {};
  for (const [type, set] of seen) out[type] = set.size;
  return out;
}

export const knowledgeCoverageQueryOptions = () =>
  queryOptions({
    queryKey: ["knowledgeCoverage"] as const,
    queryFn: getKnowledgeCoverage
  });
